import { useEffect, useMemo, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import clsx from 'clsx';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSignOutAlt, faSpinner, faTimes, faUser } from "@fortawesome/free-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import Tooltip from "@mui/material/Tooltip";
import { faMoon, faSun } from "@fortawesome/free-solid-svg-icons";

import { setThemePreference } from "../../state/reducers/themeReducer";
import { RootState } from '../../state/store';
import { logoutUser } from '../../state/actions/authActions';
import { useAppDispatch } from '../../state/hooks';
import { hasAllRoles, hasAnyRole, hasArea, isSuperZoneAdmin } from '../../utilities/helpers/roleUtilities';
import { UserDto } from '../../DTOs/User/UserDto';
import { RoleConfig } from '../../DTOs/Settings/RoleConfigDto';
import { AppRouteConfig, createAppRoutes } from "../../utilities/appRoutes";
import { getRouteIcon } from "../../utilities/routeIcons";
import { ModalContextType, useGlobalModal } from "../Contexts/GlobalModalContext";
import { useTimezone } from "../Providers/TimezoneProvider";
import camelToCapitalized from '../../utilities/helpers/camelCaseToCapitalized';
import AnalogClock from './AnalogClock';
import { findActiveNav, isNavItemActive, menuItemKey } from './navActive';

interface MenuItem {
  label: string;
  to?: string;
  onClick?: () => void; // instead of linking
  icon?: IconDefinition;
  subItems?: MenuItem[];
}

const NAV_ITEM_BASE =
  "w-full px-3 py-2 border-l-4 text-left text-theme-primary-contrast transition hover:bg-theme-primary-hover";
const NAV_LINK = `${NAV_ITEM_BASE} flex items-center gap-2`;
const NAV_CATEGORY = `${NAV_ITEM_BASE} flex justify-between items-center cursor-pointer`;
const NAV_STRIP_IDLE = "border-transparent";
const NAV_STRIP_ACTIVE = "border-theme-primary-contrast bg-theme-primary-hover font-semibold";
const NAV_STRIP_TRAIL = "border-theme-primary-contrast/40";

// Get a label from a route path
const pathToLabel = (path: string) =>
  path
    .split('/')
    .filter(Boolean)
    .map(s => s.replace(/-/g, ' '))
    .map(s => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' > ');

// Check user has permission for a route
const isRouteAllowed = (route: AppRouteConfig, user: UserDto | null, roleConfig: RoleConfig | null) => {
  if (user && hasAnyRole(user, "SuperAdmin")) return true;
  if (!route.protected) return true;
  if (!user) return false;
  if (!route.area && !route.access && !route.accessAll && !route.superZoneAdminOnly) return true;
  const areaAllowed = !route.area || hasArea(user, route.area);
  const anyRoleAllowed = !route.access || hasAnyRole(user, route.access);
  const allRolesRequired = !route.accessAll || hasAllRoles(user, route.accessAll);
  const superZoneAdminAllowed = !route.superZoneAdminOnly || isSuperZoneAdmin(user, roleConfig);
  return areaAllowed && anyRoleAllowed && allRolesRequired && superZoneAdminAllowed;
};

// Recursively convert route config to menu structure
const buildMenu = (
  routes: AppRouteConfig[],
  user: UserDto | null,
  roleConfig: RoleConfig | null,
  showModal: ModalContextType["showModal"],
  parentPath = ''
): MenuItem[] => {
  const menu: MenuItem[] = [];

  for (const route of routes) {
    if (!route.path || route.path === '*' || route.ignore) continue;
    if (!isRouteAllowed(route, user, roleConfig)) continue;

    const fullPath = `${parentPath}/${route.path}`.replace(/\/+/g, '/'); // Normalize slashes
    const hasChildren = route.children && route.children.length > 0;
    const subItems = hasChildren ? buildMenu(route.children!, user, roleConfig, showModal, fullPath) : [];

    const isGroupOnly = !route.element && subItems.length > 0;

    // Note: a pure grouping route (no element of its own, e.g. "Geology" or "Admin") derives its
    // visibility entirely from whether any child survived the access check above, so it is skipped when
    // none did - not merely when it has no children declared.
    if (!route.element && !route.action && subItems.length === 0) continue;

    const sectionOverride = parentPath === '' ? roleConfig?.sectionNames?.[route.path] : undefined;

    const menuItem: MenuItem = {
      label: sectionOverride || route.label || pathToLabel(route.path),
      icon: route.icon ?? getRouteIcon(fullPath),
      ...(route.action
        ? {
          onClick: () => route.action!(showModal)
        }
        : isGroupOnly ? {} : { to: fullPath }),
      ...(subItems.length > 0 ? { subItems } : {})
    };

    menu.push(menuItem);
  }

  return menu;
};

const Header: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, isAuthLoading, user, roleConfig } = useSelector((s: RootState) => s.authReducer);
  const currentView = useSelector((s: RootState) => s.view.currentView);
  const themePref = useSelector((s: RootState) => s.theme.preference);
  const { showModal } = useGlobalModal();
  const { timezone } = useTimezone();
  const { pathname } = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);
  // Categories default to open when they contain the current page; this records explicit user toggles.
  const [collapseOverrides, setCollapseOverrides] = useState<Record<string, boolean>>({});
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  const [now, setNow] = useState(() => DateTime.now());
  useEffect(() => {
    const id = setInterval(() => setNow(DateTime.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const mineNow = timezone ? now.setZone(timezone) : now;

  const [clockMode, setClockMode] = useState<'digital' | 'analog'>(() =>
    (localStorage.getItem('mineTimeClockMode') as 'digital' | 'analog') ?? 'digital'
  );

  const toggleClockMode = () => {
    const next = clockMode === 'digital' ? 'analog' : 'digital';
    setClockMode(next);
    localStorage.setItem('mineTimeClockMode', next);
  };

  const openMenu = () => setMenuOpen(true);

  const closeMenu = () => {
    menuButtonRef.current?.focus();
    setMenuOpen(false);
  };

  const toggleMenu = () => {
    if (menuOpen) {
      closeMenu();
    } else {
      openMenu();
    }
  };

  const toggleTheme = () => {
    dispatch(setThemePreference(themePref === "dark" ? "light" : "dark"));
  };

  const themeIcon = themePref === "dark" ? faMoon : faSun;
  const themeLabel = themePref === "dark" ? "Theme: Dark" : "Theme: Light";

  const handleLogout = (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch(logoutUser());
    closeMenu();
  };

  const routes = useMemo(() => roleConfig ? createAppRoutes(roleConfig) : [], [roleConfig]);
  const menuItems = useMemo(() => buildMenu(routes, user, roleConfig, showModal), [routes, user, roleConfig, showModal]);
  const { activeTo, trail } = useMemo(() => findActiveNav(menuItems, pathname), [menuItems, pathname]);

  const isExpanded = (key: string) => collapseOverrides[key] ?? trail.has(key);

  const toggleCategory = (key: string) => {
    const next = !isExpanded(key);
    setCollapseOverrides(prev => ({ ...prev, [key]: next }));
  };

  const renderMenuItems = (items: MenuItem[], depth = 0, parentKey = '') => {
    return (
      <ul className={`${depth > 0 ? "pl-3 py-2 mt-1 mb-2 border-l border-theme-border ml-2" : ""}`}>
        {items.map(item => {
          const itemKey = menuItemKey(parentKey, item.label);
          const isActive = isNavItemActive(item.to, activeTo);

          return (
            <li key={itemKey} className={`${depth > 0 ? "" : "border-t border-theme-border first:border-t-0"}`}>
              {item.subItems ? (
                <>
                  <button
                    type="button"
                    onClick={() => toggleCategory(itemKey)}
                    aria-expanded={isExpanded(itemKey)}
                    className={clsx(NAV_CATEGORY, isActive ? NAV_STRIP_ACTIVE : trail.has(itemKey) ? NAV_STRIP_TRAIL : NAV_STRIP_IDLE)}
                  >
                    <span className="flex items-center gap-2">
                      {item.icon && <FontAwesomeIcon icon={item.icon} fixedWidth className="opacity-80" />}
                      {item.label}
                    </span>
                    <span className="ml-3 shrink-0">{isExpanded(itemKey) ? "▾" : "▸"}</span>
                  </button>
                  {isExpanded(itemKey) && renderMenuItems(item.subItems, depth + 1, itemKey)}
                </>
              ) : item.onClick ? (
                <button
                  onClick={() => {
                    item.onClick?.();
                    closeMenu();
                  }}
                  className={clsx(NAV_LINK, NAV_STRIP_IDLE)}
                >
                  {item.icon && <FontAwesomeIcon icon={item.icon} fixedWidth className="opacity-80" />}
                  {item.label}
                </button>
              ) : (
                <Link
                  to={item.to!}
                  onClick={closeMenu}
                  aria-current={isActive ? "page" : undefined}
                  className={clsx(NAV_LINK, isActive ? NAV_STRIP_ACTIVE : NAV_STRIP_IDLE)}
                >
                  {item.icon && <FontAwesomeIcon icon={item.icon} fixedWidth className="opacity-80" />}
                  {item.label}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  const currentViewDisplay = camelToCapitalized(currentView || '');

  useEffect(() => {
    if (!menuOpen) return;
    closeButtonRef.current?.focus();

    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = original;
    };
  }, [menuOpen]);

  return (
    <>
      <header className="bg-theme-primary text-theme-primary-contrast border-b border-theme-border relative transition duration-150 sticky top-0 z-30 overflow-hidden">
        <nav className="flex items-center justify-between px-4 py-3 short:py-1">
          <div className="flex items-center gap-2 md:gap-4">
            <button
              ref={menuButtonRef}
              onClick={toggleMenu}
              aria-label="Toggle menu"
              className="flex flex-col justify-center items-center gap-1 w-8 h-8 short:w-6 short:h-6 p-1 mr-2 cursor-pointer"
            >
              <span className="block w-full h-1 short:h-0.5 bg-theme-primary-contrast rounded" />
              <span className="block w-full h-1 short:h-0.5 bg-theme-primary-contrast rounded" />
              <span className="block w-full h-1 short:h-0.5 bg-theme-primary-contrast rounded" />
            </button>
            <h2 className="font-semibold text-sm short:text-[0.8rem] md:text-lg">{currentViewDisplay}</h2>
          </div>

          <div className="flex-none space-x-4 flex gap-2 items-center transition">
            <Tooltip title={clockMode === 'digital' ? 'Switch to clock face' : 'Switch to digital'} arrow>
              <button
                type="button"
                onClick={toggleClockMode}
                className="hidden sm:flex items-center gap-2 opacity-75 hover:opacity-100 transition cursor-pointer select-none"
                aria-label={clockMode === 'digital' ? 'Switch to clock face' : 'Switch to digital'}
              >
                {!timezone ? (
                  clockMode === 'digital' ? (
                    <span className="font-mono tabular-nums animate-pulse select-none">Mine Time ‒‒:‒‒:‒‒</span>
                  ) : (
                    <>
                      <span className="font-mono tabular-nums text-sm animate-pulse select-none">Mine Time</span>
                      <div className="w-8 h-8 rounded-full animate-pulse bg-current opacity-30" />
                    </>
                  )
                ) : clockMode === 'digital' ? (
                  <span className="font-mono tabular-nums">Mine Time {mineNow.toFormat('HH:mm:ss')}</span>
                ) : (
                  <>
                    <span className="font-mono tabular-nums text-sm">Mine Time</span>
                    <AnalogClock hours={mineNow.hour} minutes={mineNow.minute} seconds={mineNow.second} />
                  </>
                )}
              </button>
            </Tooltip>
            <Tooltip title={themeLabel} arrow>
              <button
                type="button"
                onClick={toggleTheme}
                className="flex items-center justify-center transition duration-150 hover:text-theme-primary-contrast-hover cursor-pointer"
                aria-label={themeLabel}
              >
                <FontAwesomeIcon icon={themeIcon} className="!w-6 !h-6 short:!w-4 short:!h-4 sm:!w-5 sm:!h-5" />
              </button>
            </Tooltip>

            {isAuthLoading ? (
              <span className={`transition opacity-${isAuthLoading ? "100" : "0"}`}>
                <FontAwesomeIcon icon={faSpinner} spin />
              </span>
            ) : (
              isAuthenticated && (
                <Link
                  to="/account"
                  className={`font-semibold hover:text-theme-primary-contrast-hover transition duration-150 opacity-${isAuthLoading ? "0" : "100"}`}
                >
                  <span className="!hidden sm:!inline short:text-[0.8rem]">{user?.name ?? "User"}</span>
                  <FontAwesomeIcon icon={faUser} className="!block sm:!hidden !w-6 !h-6 short:!w-4 short:!h-4" />
                </Link>
              )
            )}

            {isAuthenticated ? (
              <button onClick={handleLogout} className="transition duration-150 hover:text-theme-primary-contrast-hover cursor-pointer">
                <span className="!hidden sm:!inline short:text-[0.8rem]">Logout</span>
                <FontAwesomeIcon icon={faSignOutAlt} className="!block sm:!hidden !w-6 !h-6 short:!w-4 short:!h-4" />
              </button>
            ) : (
              <Link to="/login" onClick={() => setMenuOpen(false)} className="transition duration-150 hover:text-theme-primary-contrast-hover">
                Login
              </Link>
            )}
          </div>
        </nav>
      </header>

      <div
        className={`fixed inset-0 z-50 transition duration-150 ${menuOpen ? "pointer-events-auto" : "pointer-events-none"}`}
        aria-hidden={!menuOpen}
      >
        <div
          className={`absolute inset-0 bg-black/40 transition-opacity duration-150 ${menuOpen ? "opacity-100" : "opacity-0"}`}
          onClick={closeMenu}
        />

        <aside
          className={`absolute top-0 left-0 h-full w-[22rem] max-w-[90vw] bg-theme-primary text-theme-primary-contrast border-theme-border transition-transform duration-150 flex flex-col ${menuOpen ? "translate-x-0 border-r shadow-2xl shadow-theme-border" : "-translate-x-full"}`}
        >
          <div className="flex items-center justify-between px-4 py-4 short:py-1 border-b border-theme-primary-contrast shrink-0">
            <button
              ref={closeButtonRef}
              onClick={closeMenu}
              className="text-3xl short:text-2xl leading-none hover:text-theme-primary-contrast-hover transition cursor-pointer"
              aria-label="Close menu"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <h3 className="font-semibold text-lg short:text-[0.8rem]">Menu</h3>
          </div>

          <div className="flex-1 overflow-y-auto overscroll-contain">
            {renderMenuItems(menuItems)}
          </div>
        </aside>
      </div>
    </>
  );
};

export default Header;
